import {SyncStatus} from './entity.js';

export const LOCAL_DB_NAME='dymistrz_local';
export const LOCAL_DB_VERSION=1;
export const LOCAL_ENTITY_STORE='entities';

const PENDING=new Set([SyncStatus.PENDING_CREATE,SyncStatus.PENDING_UPDATE,SyncStatus.PENDING_DELETE,SyncStatus.CONFLICT]);

function migrateToV1(db,transaction){
  const store=db.objectStoreNames.contains(LOCAL_ENTITY_STORE)
    ?transaction.objectStore(LOCAL_ENTITY_STORE)
    :db.createObjectStore(LOCAL_ENTITY_STORE,{keyPath:'key'});
  if(!store.indexNames.contains('syncStatus'))store.createIndex('syncStatus','syncStatus',{unique:false});
}

export function applyLocalMigrations({db,transaction,oldVersion=0,newVersion=LOCAL_DB_VERSION}={}){
  if(!db||!transaction)throw new TypeError('IndexedDB migration context is required');
  if(oldVersion<1&&newVersion>=1)migrateToV1(db,transaction);
  // Future migrations are appended here as monotonic version steps:
  // if(oldVersion<2&&newVersion>=2)migrateToV2(db,transaction);
}

export function createIndexedDbRepository({indexedDB=globalThis.indexedDB,databaseName=LOCAL_DB_NAME,version=LOCAL_DB_VERSION}={}){
  if(!indexedDB?.open)throw new Error('IndexedDB is unavailable');
  if(!databaseName.startsWith('dymistrz_'))throw new Error('Local database namespace must be Dymistrz-specific');
  if(!Number.isInteger(version)||version<1)throw new TypeError('IndexedDB version must be a positive integer');

  let dbPromise;
  function open(){
    if(dbPromise)return dbPromise;
    dbPromise=new Promise((resolve,reject)=>{
      const request=indexedDB.open(databaseName,version);
      request.onupgradeneeded=event=>{
        applyLocalMigrations({
          db:request.result,
          transaction:request.transaction,
          oldVersion:Number(event?.oldVersion)||0,
          newVersion:Number(event?.newVersion)||version
        });
      };
      request.onsuccess=()=>{
        const db=request.result;
        db.onversionchange=()=>{try{db.close();}finally{dbPromise=undefined;}};
        resolve(db);
      };
      request.onerror=()=>{dbPromise=undefined;reject(request.error||new Error('IndexedDB open failed'));};
      request.onblocked=()=>{dbPromise=undefined;reject(new Error('IndexedDB upgrade is blocked'));};
    });
    return dbPromise;
  }

  async function transact(mode,operation){
    const db=await open();
    return new Promise((resolve,reject)=>{
      const tx=db.transaction(LOCAL_ENTITY_STORE,mode),store=tx.objectStore(LOCAL_ENTITY_STORE);
      let result;
      try{result=operation(store);}catch(error){reject(error);return;}
      tx.oncomplete=()=>resolve(result?.result);
      tx.onerror=()=>reject(tx.error||new Error('IndexedDB transaction failed'));
      tx.onabort=()=>reject(tx.error||new Error('IndexedDB transaction aborted'));
    });
  }

  const key=(type,id)=>`${type}:${id}`;
  return Object.freeze({
    databaseName,
    version,
    async get(type,id){return transact('readonly',store=>store.get(key(type,id)));},
    async list(type){
      if(!type)throw new TypeError('type is required');
      const db=await open();
      return new Promise((resolve,reject)=>{
        const tx=db.transaction(LOCAL_ENTITY_STORE,'readonly'),store=tx.objectStore(LOCAL_ENTITY_STORE),request=store.getAll();
        request.onsuccess=()=>resolve((request.result||[])
          .filter(item=>item.entityType===type)
          .sort((a,b)=>String(a.createdAt||'').localeCompare(String(b.createdAt||''))||String(a.id||'').localeCompare(String(b.id||''))));
        request.onerror=()=>reject(request.error||new Error('IndexedDB read failed'));
      });
    },
    async put(type,entity){
      if(!type||!entity?.id)throw new TypeError('type and entity.id are required');
      const record={...entity,key:key(type,entity.id),entityType:type};
      await transact('readwrite',store=>store.put(record));
      return record;
    },
    async remove(type,id){
      if(!type||!id)throw new TypeError('type and id are required');
      await transact('readwrite',store=>store.delete(key(type,id)));
    },
    async listPending(){
      const db=await open();
      return new Promise((resolve,reject)=>{
        const tx=db.transaction(LOCAL_ENTITY_STORE,'readonly'),store=tx.objectStore(LOCAL_ENTITY_STORE),request=store.getAll();
        request.onsuccess=()=>resolve((request.result||[])
          .filter(item=>PENDING.has(item.syncStatus))
          .sort((a,b)=>String(a.updatedAt||'').localeCompare(String(b.updatedAt||''))||String(a.id||'').localeCompare(String(b.id||''))));
        request.onerror=()=>reject(request.error||new Error('IndexedDB read failed'));
      });
    },
    async close(){
      if(!dbPromise)return;
      const db=await dbPromise;
      db.close();
      dbPromise=undefined;
    }
  });
}
